import { useModals, Box, DotSeparator, Stack, Text } from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployee, ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { EmployeeAvatar } from "./EmployeeAvatar.tsx";

type Size = "sm" | "md";

export interface EmployeeItemProps {
  employee: ApiEmployee;
  variant?: keyof typeof theme.variants;
  size?: Size;
  showPosition?: boolean;
  direction?: "horizontal" | "vertical";
  caption?: React.ReactNode;
  onClick?: () => void;
  enableLink?: boolean;
  enablePreview?: boolean;
  target?: "_blank" | "_self" | "_parent" | "_top";
  truncate?: boolean;
}

const theme = {
  variants: {
    primary: "text-title",
    secondary: "text-secondary",
    light: "text-light",
  },
};

export const EmployeeItem = ({
  employee,
  size = "md",
  variant = "primary",
  showPosition = true,
  direction = "vertical",
  caption,
  onClick,
  enableLink = false,
  enablePreview = false,
  target = "_blank",
  truncate = false,
}: EmployeeItemProps) => {
  const { renderEmployeeName } = useFormatter();
  const position = getEmployeePositionTitle(employee);
  const { openModal } = useModals();
  const isInteractive = (enableLink || enablePreview) && !employee.is_archived;

  const captionsElements = React.Children.toArray(caption).filter(Boolean);

  const content = (
    <Stack
      direction={"horizontal"}
      gap={"sm"}
      items={"center"}
      onClick={onClick}
      className={twMerge(
        onClick && "cursor-pointer",
        employee.lifecycle_status === ApiEmployeeLifecycleStatus.terminated && "opacity-60",
        "grow"
      )}
    >
      <EmployeeAvatar size={size} employee={employee} />
      {direction == "vertical" && (
        <Stack className={twMerge(truncate && "truncate")}>
          <Text
            className={twMerge(
              theme.variants[variant],
              truncate && "truncate",
              isInteractive && "hover:underline"
            )}
          >
            {renderEmployeeName(employee)}
          </Text>
          {showPosition && (
            <Text className={twMerge("text-secondary", truncate && "truncate")}>
              {position || "-"}
            </Text>
          )}
          {captionsElements.length > 0 &&
            captionsElements.map((item, index) => (
              <Text key={index} className={twMerge("text-secondary", truncate && "truncate")}>
                {item}
              </Text>
            ))}
        </Stack>
      )}
      {direction == "horizontal" && (
        <DotSeparator className={twMerge(truncate && "truncate")}>
          <Text
            className={twMerge(
              theme.variants[variant],
              truncate && "truncate",
              isInteractive && "hover:underline"
            )}
          >
            {renderEmployeeName(employee)}
          </Text>
          {showPosition && (
            <Text className={twMerge("text-secondary", truncate && "truncate")}>
              {position || "-"}
            </Text>
          )}
          {captionsElements.length > 0 &&
            captionsElements.map((item, index) => <Fragment key={index}>{item}</Fragment>)}
        </DotSeparator>
      )}
    </Stack>
  );
  if (enablePreview && !employee.is_archived) {
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (enableLink && (event.metaKey || event.ctrlKey || event.button === 1)) {
        event.preventDefault();
        window.open(`/employees/${employee.id}`, target);
        return;
      }

      openModal(EmployeeViewModal, {
        employee: employee,
      });
    };

    return (
      <Box
        key={employee.id}
        className={"cursor-pointer"}
        onClick={handleClick}
        onAuxClick={handleClick}
      >
        {content}
      </Box>
    );
  }
  if (enableLink && !employee.is_archived) {
    return (
      <Link key={employee.id} to={`/employees/${employee.id}`} target={target}>
        {content}
      </Link>
    );
  }

  return content;
};
